/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, July 21st 2024, 3:11:37 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import TranslateIcon from "@mui/icons-material/Translate";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { TLocales } from "lib/schemas";
import { useTranslationAPI } from "translation/services";
import { useOSFormContext } from "ui-widgets/forms/providers";
import {
  OSFormInputProps,
  TextFormInput,
  TextFormInputProps,
  TextAreaFormInput,
  StyledOSDialog,
  TabbedNavigator,
  APIItemDisplayWrapper,
} from "ui-widgets";

type TranslatableInputWrapperProps<T> = {
  children: React.ReactNode;
  fieldKey: keyof T & string;
  fieldValue: string;
  setTranslatedValue: (translatedFieldKey: string, translatedFieldValue: string) => void;
  getTranslatedValue: (translatedFieldKey: string) => string;
  translationInputType?: "input" | "textarea";
  fieldLabel?: string;
};

export const TranslatableInputWrapper = <T,>({
  children,
  fieldLabel,
  ...localeTranslationProps
}: TranslatableInputWrapperProps<T>) => {
  const { locales, isMakingAPIRequest: isFetchingLocales, getSupportedLocales } = useTranslationAPI().useGetLanguages();

  const onClickTranslate = () => {
    getSupportedLocales();
  };

  return (
    <Stack direction="row" sx={{ width: "100%" }}>
      <div style={{ flex: 20 }}>{children}</div>
      <div style={{ flex: 1, paddingTop: 6 }}>
        <StyledOSDialog
          popUpTitle={`Translate${fieldLabel ? ` ${fieldLabel}` : ""}`}
          popUpIcon={<TranslateIcon color="primary" />}
          renderOpenButton={({ handleOpen }) => (
            <Tooltip title={<Typography>Translate</Typography>}>
              <IconButton
                color="primary"
                onClick={() => {
                  onClickTranslate();
                  handleOpen();
                }}
              >
                <TranslateIcon />
              </IconButton>
            </Tooltip>
          )}
        >
          <APIItemDisplayWrapper showLoader={isFetchingLocales} loaderType="linear">
            <LocaleTranslation<T> locales={locales} {...localeTranslationProps} />
          </APIItemDisplayWrapper>
        </StyledOSDialog>
      </div>
    </Stack>
  );
};

type LocaleTranslationProps<T> = Omit<TranslatableInputWrapperProps<T>, "children"> & {
  locales: TLocales;
};

const LocaleTranslation = <T,>({
  locales,
  fieldKey,
  translationInputType,
  fieldValue,
  getTranslatedValue,
  setTranslatedValue,
}: LocaleTranslationProps<T>) => {
  const tabbedContent = [];

  Object.keys(locales).forEach((locale) => {
    const virtualFieldKey = `${fieldKey}_${locale}`;
    const onChangeTranslatedValue = (value: string) => setTranslatedValue(virtualFieldKey, value);
    const translatableFieldLabel = fieldValue
      ? `'${fieldValue}' in ${locales[locale]}`
      : `Translate to ${locales[locale]}`;

    tabbedContent.push(
      <div>
        <Tooltip title={<Typography>{locales[locale]}</Typography>}>
          <div>{locale}</div>
        </Tooltip>
      </div>
    );
    tabbedContent.push(
      <Stack>
        <TextField
          label={translatableFieldLabel}
          defaultValue={getTranslatedValue(virtualFieldKey)}
          onChange={(event) => onChangeTranslatedValue(event.target.value)}
          multiline={translationInputType === "textarea"}
          rows={translationInputType === "textarea" ? 4 : 1}
        />
      </Stack>
    );
  });

  return <TabbedNavigator variant="fullWidth">{tabbedContent}</TabbedNavigator>;
};

type TWithTransHOCOptions<T> = Omit<TranslatableInputWrapperProps<T>, "children">;

export const withTranslation =
  <P,>(Component: React.ComponentType<P>, options: TWithTransHOCOptions<any>) =>
  (props: P) => {
    return <TranslatableInputWrapper<any> {...options}>{<Component {...props} />}</TranslatableInputWrapper>;
  };

export const withTranslationForFormInput =
  <T,>(Component: React.ComponentType<OSFormInputProps<T>>, options?: Partial<TWithTransHOCOptions<T>>) =>
  (props: OSFormInputProps<T>) => {
    const { watch, setValue } = useOSFormContext<any>();
    return withTranslation(Component, {
      ...options,
      fieldKey: props.schema_field_key,
      fieldLabel: props.label,
      fieldValue: watch(props.schema_field_key as any) as string,
      getTranslatedValue: (virtualFieldKey: string) => {
        return watch(virtualFieldKey as any) as string;
      },
      setTranslatedValue: (translatedFieldKey, translatedFieldValue) =>
        setValue(translatedFieldKey as any, translatedFieldValue),
    })(props);
  };

export const TranslatableTextFormInput = withTranslationForFormInput(TextFormInput) as <T>(
  props: TextFormInputProps<T>
) => JSX.Element;

export const TranslatableTextAreaInput = withTranslationForFormInput(TextAreaFormInput, {
  translationInputType: "textarea",
}) as <T>(props: TextFormInputProps<T>) => JSX.Element;
